import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Wallet, CheckCircle2, Clock, Receipt, Filter } from "lucide-react";

const brl = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
const dt = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "—");

const NF_CLASS = {
  nao_anexada: "bg-[#E60000] text-white", rejeitada: "bg-[#E60000] text-white",
  anexada: "bg-[#142766] text-white", aprovada: "bg-[#00A859] text-white",
  paga: "bg-foreground text-white", acumulada: "bg-[#FFCC00] text-foreground",
  nao_gerada: "bg-aj-surface text-aj-text-muted border border-aj-border",
};

function BlocoNF({ nf }) {
  if (!nf) return null;
  return (
    <div className="mt-2 space-y-1.5" data-testid="fin-grupo-nf">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${NF_CLASS[nf.situacao] || "bg-aj-surface"}`}
          data-testid="fin-grupo-nf-status">{nf.situacao_label}{nf.nf_numero ? ` · nº ${nf.nf_numero}` : ""}</span>
        {nf.prazo && (
          <span data-testid="fin-grupo-nf-prazo">Prazo para envio: <strong>{dt(nf.prazo)}</strong></span>
        )}
        {nf.precisa_envio && nf.id && (
          <Link to="/montador/notas-fiscais" className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold btn-sharp brand-bg text-white"
            data-testid="fin-grupo-enviar-nf">
            <Receipt className="w-3 h-3" /> Enviar Nota Fiscal
          </Link>
        )}
      </div>
      {nf.mensagem && (
        <div className={`border-l-4 p-2 text-xs ${nf.alerta === "vermelho" ? "border-[#E60000] bg-red-50 text-[#E60000] font-semibold" : "border-[#FFCC00] bg-amber-50 text-amber-800"}`}
          data-testid={nf.alerta === "vermelho" ? "fin-grupo-nf-alerta" : "fin-grupo-nf-aviso"}>
          {nf.mensagem}
        </div>
      )}
    </div>
  );
}

function CabecalhoGrupo({ g }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-4" data-testid={`fin-grupo-header-${g.empresa_id}-${g.periodo}`}>
      <div className="min-w-0">
        <div className="label-cap text-[#142766]" data-testid="fin-grupo-empresa">{g.empresa_nome}</div>
        <div className="font-heading font-extrabold text-xl mt-1" data-testid="fin-grupo-periodo">Período {g.periodo_label}</div>
        <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-4 gap-y-1">
          <span data-testid="fin-grupo-fechamento">Fechamento: <strong>{dt(g.fechamento)}</strong></span>
          <span data-testid="fin-grupo-pagamento">
            Pagamento previsto: <strong>{g.calendario_configurado && g.pagamento_previsto ? dt(g.pagamento_previsto) : "Calendário de pagamento não configurado"}</strong>
          </span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <span data-testid="fin-grupo-exige-nf">Exige NF: <strong>{g.exige_nf ? "Sim" : "Não"}</strong></span>
        </div>
        {g.exige_nf && <BlocoNF nf={g.nf} />}
      </div>
      <div className="grid grid-cols-3 gap-2 text-right flex-shrink-0">
        <div><div className="label-cap">Total</div><div className="font-mono font-bold text-sm" data-testid="fin-grupo-total">{brl(g.total)}</div></div>
        <div><div className="label-cap">Pago</div><div className="font-mono font-bold text-sm text-[#00A859]" data-testid="fin-grupo-total-pago">{brl(g.total_pago)}</div></div>
        <div><div className="label-cap">A pagar</div><div className="font-mono font-bold text-sm text-[#E60000]" data-testid="fin-grupo-total-a-pagar">{brl(g.total_a_pagar)}</div></div>
      </div>
    </div>
  );
}

function TabelaLinhas({ linhas }) {
  return (
    <div className="overflow-x-auto -mx-5 px-5">
      <table className="w-full text-xs min-w-[640px]">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
            <th className="py-2 pr-3">OS</th><th className="py-2 pr-3">Produto</th><th className="py-2 pr-3">Tipo de serviço</th>
            <th className="py-2 pr-3 text-right">Valor a receber</th><th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3">Data prevista</th><th className="py-2">Data paga</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => (
            <tr key={l.servico_id} className="border-b border-border/60 last:border-0" data-testid={`fin-linha-${l.servico_id}`}>
              <td className="py-2 pr-3 font-mono font-semibold" data-testid="fin-linha-os">{l.pim_code}</td>
              <td className="py-2 pr-3 max-w-[220px] truncate" title={l.produto} data-testid="fin-linha-produto">{l.produto}</td>
              <td className="py-2 pr-3" data-testid="fin-linha-tipo">{l.tipo_label}</td>
              <td className="py-2 pr-3 text-right font-mono font-bold" data-testid="fin-linha-valor">{brl(l.valor)}</td>
              <td className="py-2 pr-3">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider ${l.status === "pago" ? "bg-[#00A859] text-white" : "bg-[#FFCC00]"}`}
                  data-testid="fin-linha-status">
                  {l.status === "pago" ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                  {l.status === "pago" ? "Pago" : "A pagar"}
                </span>
              </td>
              <td className="py-2 pr-3" data-testid="fin-linha-data-prevista">{dt(l.data_prevista)}</td>
              <td className="py-2" data-testid="fin-linha-data-paga">{dt(l.data_paga)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MontadorFinanceiro() {
  const [dados, setDados] = useState(null);
  const [filtros, setFiltros] = useState({ empresa_id: "", periodo: "", status: "" });
  const [erro, setErro] = useState("");

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(filtros).filter(([, v]) => v));
    api.get("/montador/financeiro", { params }).then((r) => { setDados(r.data); setErro(""); })
      .catch(() => setErro("Não foi possível carregar seu financeiro."));
  }, [filtros]);

  const t = dados?.totais || { total: 0, pago: 0, a_pagar: 0 };
  const sel = "h-9 px-2 text-xs border border-border bg-white min-w-[160px]";

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="montador-financeiro">
      <div className="flex items-center gap-2">
        <Wallet className="w-5 h-5 text-aj-orange" />
        <h1 className="font-heading font-extrabold text-2xl text-aj-blue-deep">Meu Financeiro</h1>
      </div>
      <p className="text-xs text-muted-foreground">Valores calculados pelo sistema para os seus serviços, agrupados por Empresa e período de fechamento. Somente leitura.</p>

      <div className="grid grid-cols-3 gap-3">
        <div className="border border-border bg-white p-4"><div className="label-cap">Total</div>
          <div className="font-heading font-extrabold text-2xl font-mono" data-testid="fin-total-geral">{brl(t.total)}</div></div>
        <div className="border border-border bg-white p-4"><div className="label-cap">Pago</div>
          <div className="font-heading font-extrabold text-2xl font-mono text-[#00A859]" data-testid="fin-total-pago">{brl(t.pago)}</div></div>
        <div className="border border-border bg-white p-4"><div className="label-cap">A pagar</div>
          <div className="font-heading font-extrabold text-2xl font-mono text-[#E60000]" data-testid="fin-total-a-pagar">{brl(t.a_pagar)}</div></div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border border-border bg-white p-3" data-testid="fin-filtros">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <select className={sel} value={filtros.empresa_id} onChange={(e) => setFiltros({ ...filtros, empresa_id: e.target.value })} data-testid="fin-filtro-empresa">
          <option value="">Todas as Empresas</option>
          {(dados?.empresas || []).map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
        <select className={sel} value={filtros.periodo} onChange={(e) => setFiltros({ ...filtros, periodo: e.target.value })} data-testid="fin-filtro-periodo">
          <option value="">Todos os períodos</option>
          {(dados?.periodos || []).map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <select className={sel} value={filtros.status} onChange={(e) => setFiltros({ ...filtros, status: e.target.value })} data-testid="fin-filtro-status">
          <option value="">Pago e A pagar</option>
          <option value="a_pagar">A pagar</option>
          <option value="pago">Pago</option>
        </select>
      </div>

      {erro && <div className="border border-[#E60000] bg-red-50 p-3 text-xs text-[#E60000]" data-testid="fin-erro">{erro}</div>}
      {dados && dados.grupos.length === 0 && (
        <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground" data-testid="fin-vazio">
          Nenhum lançamento financeiro encontrado para os filtros selecionados.
        </div>
      )}
      {(dados?.grupos || []).map((g) => (
        <div key={`${g.empresa_id}-${g.periodo}`} className="border-2 border-border bg-white p-5" data-testid={`fin-grupo-${g.empresa_id}-${g.periodo}`}>
          <CabecalhoGrupo g={g} />
          <TabelaLinhas linhas={g.linhas} />
        </div>
      ))}
    </div>
  );
}
